import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, Send, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

export default function AdminAlerts() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/alerts/config");
    setCfg(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/admin/alerts/config", cfg);
      toast.success("Alert settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const runNow = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/alerts/test");
      toast.success(`Reminder scan complete — ${data.reminders_sent} reminder(s) sent`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-zinc-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-alerts-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Notifications</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Low-balance reminders</h1>
        <p className="text-zinc-500 mt-1">
          When a reseller's diamond balance drops below the threshold, automatically send them a
          recharge reminder on WhatsApp and/or their registered email.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Low-balance threshold (💎 diamonds)</Label>
          <Input type="number" min="0" value={cfg.low_balance_threshold}
            onChange={(e) => set("low_balance_threshold", e.target.value)} data-testid="threshold-input" className="max-w-xs" />
          <p className="text-xs text-zinc-500 mt-1">Reminder is sent once when a reseller's balance for a platform falls below this. Recommended: 1000.</p>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-500" />
            <div>
              <div className="font-medium text-zinc-900">WhatsApp reminder</div>
              <div className="text-xs text-zinc-500">Sent to the reseller's WhatsApp number.</div>
            </div>
          </div>
          <Switch checked={!!cfg.whatsapp_enabled} onCheckedChange={(v) => set("whatsapp_enabled", v)} data-testid="whatsapp-enabled-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-500" />
            <div>
              <div className="font-medium text-zinc-900">Email reminder</div>
              <div className="text-xs text-zinc-500">Sent to the reseller's registered email.</div>
            </div>
          </div>
          <Switch checked={!!cfg.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="email-enabled-switch" />
        </div>

        <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3 leading-relaxed">
          Reminders use your configured Resend (email) and Meta WhatsApp credentials from
          <strong> OTP / Verification</strong>. WhatsApp free-text delivery requires an active 24-hour session with the reseller;
          email always delivers.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={loading} className="bg-zinc-900 hover:bg-zinc-800" data-testid="save-alerts-button">
          <Bell className="w-4 h-4 mr-1" /> {loading ? "Saving…" : "Save settings"}
        </Button>
        <Button onClick={runNow} disabled={testing} variant="outline" data-testid="test-alerts-button">
          <Send className="w-4 h-4 mr-1" /> {testing ? "Scanning…" : "Run reminder scan now"}
        </Button>
      </div>
    </div>
  );
}
